import * as T from "three";
import { Assets } from "./Assets.js";
import { World } from "./World.js";
import { Input } from "./Input.js";
import { UI } from "./UI.js";
import { Sound } from "./Sound.js";
import { normalAt, frameAt } from "./planet.js";
import { ZONES, nearestZone } from "./config.js";
export class Island {
  constructor() {
    this.events = new AbortController();
    this.listen = (target, type, handler, options = {}) =>
      target.addEventListener(type, handler, {
        ...options,
        signal: this.events.signal,
      });
    this.low = matchMedia("(max-width: 760px), (pointer: coarse)").matches;
    this.reduced = matchMedia("(prefers-reduced-motion: reduce)");
    this.assets = new Assets();
    this.sound = new Sound();
    this.cameraMode = "overview";
    this.zoom = 1;
    this.overviewPoint = [0, 26];
    this.cameraSnap = true;
    this.busy = false;
    this.interactionSteps = new Map();
    this.interacting = false;
    this.ready = false;
    this.ray = new T.Raycaster();
    this.occluded = new Set();
    this.fadeMaterials = new Map();
    this.errors = [];
    this.ui = new UI({
      travel: (id) => this.travel(id),
      interact: () => this.interact(),
      details: () => this.toggleDetails(),
      lock: (v) => this.input?.setLocked(v),
      effect: (id, index) => this.effect(id, index),
      sound: async () => {
        try {
          this.ui.sound(await this.sound.toggle());
        } catch {
          this.ui.toast("声音暂时无法开启。");
        }
      },
      overview: () => {
        this.cameraMode = "overview";
        this.cameraSnap = true;
      },
      quality: () => this.setQuality(!this.low),
      retry: () => this.retry(),
    });
    try {
      this.setRenderer();
    } catch (error) {
      this.fallback(error);
      return;
    }
    this.scene = new T.Scene();
    this.scene.background = new T.Color("#edf0e4");

    this.camera = new T.OrthographicCamera(-45, 45, 30, -30, 0.1, 300);
    this.camera.position.set(42, 52, 66);
    this.camera.lookAt(0, 0, 0);
    this.lookTarget = new T.Vector3();

    this.setLights();
    this.world = new World(this.scene, this.assets, this.low);
    this.input = new Input({
      interact: () => this.interact(),
      details: () => this.toggleDetails(),
      close: () => this.ui.close(),
      home: () => this.travel("welcome"),
    });
    this.resize();
    this.ui.quality(this.low);
    this.bind();
    this.previous = performance.now();
    this.fps = [];
    this.frame = this.frame.bind(this);
    this.frameId = requestAnimationFrame(this.frame);
    this.loadInitial();
  }
  setRenderer() {
    if (
      import.meta.env.DEV &&
      new URLSearchParams(location.search).get("webgl") === "off"
    )
      throw new Error("WebGL disabled for fallback QA");
    const canvas = document.querySelector("#island-canvas");
    this.renderer = new T.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
      alpha: false,
    });
    this.renderer.outputColorSpace = T.SRGBColorSpace;
    this.renderer.toneMapping = T.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = !this.low;
    this.renderer.shadowMap.type = T.PCFSoftShadowMap;
    this.listen(canvas, "webglcontextlost", (e) => {
      e.preventDefault();
      this.fallback(new Error("WebGL context lost"));
    });
  }
  setLights() {
    const hemi = new T.HemisphereLight("#fff5df", "#8faaa0", 1.5);
    this.scene.add(hemi);
    this.sun = new T.DirectionalLight("#fff0d6", 2.1);
    this.sun.position.set(-40, 65, 65);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    Object.assign(this.sun.shadow.camera, {
      left: -40,
      right: 40,
      top: 35,
      bottom: -35,
      near: 1,
      far: 180,
    });
    this.sun.shadow.radius = 3;
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.035;
    this.scene.add(this.sun);
    this.scene.add(new T.AmbientLight("#fff4df", 0.7));
    const fill = new T.DirectionalLight("#d7efeb", 0.7);
    fill.position.set(20, 12, -20);
    this.scene.add(fill);
  }
  async loadInitial() {
    if (this.busy) return;
    this.busy = true;
    this.ui.loading("正在唤醒小女孩和小星球…");
    try {
      await Promise.all([
        this.world.avatar ? null : this.world.character(),
        this.world.zone(ZONES[0]),
        this.world.zone(ZONES[1]),
      ]);
      if (this.disposed) return;
      this.ready = true;
      document.body.classList.add("ready");
      this.ui.loading("正在布置星球上的小角落…");
      await this.loadRemaining();
    } catch (error) {
      this.errors.push(error.message);
      this.ui.loading("小星球没有加载完整，请重试。", true);
    } finally {
      this.busy = false;
    }
  }
  async loadRemaining() {
    const results = await Promise.allSettled(
      ZONES.slice(2).map((z) => this.world.zone(z)),
    );
    let errors = results.filter((r) => r.status === "rejected").length;
    if (!this.decorated) {
      errors += await this.world.decorate();
      this.decorated = errors === 0;
    }
    if (this.disposed) return;
    this.ui.loading(errors ? "部分角落还没准备好，可以重试。" : "", !!errors);
    this.renderOnce();
  }
  async retry() {
    if (this.busy) return;
    if (!this.ready) return this.loadInitial();
    this.busy = true;
    this.ui.loading("正在重新布置…");
    try {
      await this.loadRemaining();
    } finally {
      this.busy = false;
    }
  }
  bind() {
    this.onResize = () => this.resize();
    addEventListener("resize", this.onResize);
    this.onVisibility = () => {
      this.previous = performance.now();
      this.sound.visibility(document.hidden);
    };
    document.addEventListener("visibilitychange", this.onVisibility);
    const canvas = this.renderer.domElement;
    this.listen(
      canvas,
      "wheel",
      (e) => {
        if (this.ui.panel.open) return;
        e.preventDefault();
        this.zoom = T.MathUtils.clamp(this.zoom + e.deltaY * 0.0007, 0.75, 1.3);
      },
      { passive: false },
    );
    let down = null;
    this.listen(canvas, "pointerdown", (e) => {
      down = [e.clientX, e.clientY];
      this.dragPoint = [...this.overviewPoint];
    });
    this.listen(canvas, "pointermove", (e) => {
      if (
        !down ||
        !e.buttons ||
        this.cameraMode !== "overview" ||
        this.ui.panel.open
      )
        return;
      this.overviewPoint = [
        this.dragPoint[0] - (e.clientX - down[0]) * 0.25,
        T.MathUtils.clamp(
          this.dragPoint[1] + (e.clientY - down[1]) * 0.2,
          -75,
          75,
        ),
      ];
    });
    this.listen(canvas, "pointercancel", () => {
      down = null;
    });
    this.listen(canvas, "pointerup", (e) => {
      if (
        !down ||
        Math.hypot(e.clientX - down[0], e.clientY - down[1]) > 8 ||
        !this.ready ||
        this.ui.panel.open
      )
        return;
      const rect = canvas.getBoundingClientRect();
      this.ray.setFromCamera(
        new T.Vector2(
          ((e.clientX - rect.left) / rect.width) * 2 - 1,
          (-(e.clientY - rect.top) / rect.height) * 2 + 1,
        ),
        this.camera,
      );
      const hit = this.ray.intersectObjects(
        [...this.world.interactive, this.world.planet],
        true,
      )[0];
      if (hit) {
        if (!document.body.classList.contains("exploring")) {
          this.explore();
          down = null;
          return;
        }
        const z = ZONES.find((z) => z.id === hit.object.userData.zone);
        if (z) this.ui.show(z);
      }
      down = null;
    });
  }
  resize() {
    const width = innerWidth,
      height = innerHeight;
    this.renderer.setPixelRatio(
      Math.min(devicePixelRatio || 1, this.low ? 1.25 : 1.5),
    );
    this.renderer.setSize(width, height);
    this.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
  setQuality(low) {
    this.low = low;
    this.renderer.shadowMap.enabled = !low;
    this.world.quality(low);
    this.resize();
    this.ui.quality(low);
    this.ui.toast(low ? "轻量画质已开启" : "完整画质已开启");
  }
  explore() {
    this.cameraMode = "follow";
    this.ui.hideIntro();
    this.ui.toast("靠近地点后按 E 互动，I 看介绍；也可用下方地点跳转。");
  }
  toggleDetails() {
    if (this.ui.panel.open) {
      this.ui.close();
      return;
    }
    if (!this.ready) return;
    const zone = nearestZone(this.world.position) || this.ui.nearby;
    if (zone) this.ui.show(zone);
  }
  travel(id) {
    if (!this.ready) {
      this.ui.toast("小女孩马上就准备好了。");
      return;
    }
    const zone = ZONES.find((z) => z.id === id);
    if (!zone) return;
    this.input.clear();
    this.world.teleport(zone.spawn);
    this.cameraMode = "follow";
    this.cameraSnap = true;
    this.ui.hideIntro();
    this.ui.active(id);
    this.sound.chime();
    if (this.reduced.matches) this.lookTarget.copy(this.world.position);
    this.ui.setNearby(zone);
    if (!this.world.zones.has(id))
      this.ui.toast("这个角落正在加载，可稍后重试。");
  }
  async interact() {
    if (
      !this.ready ||
      this.ui.panel.open ||
      this.interacting ||
      this.world.elapsed < (this.nextInteractionAt || 0)
    )
      return;
    const zone = nearestZone(this.world.position);
    if (!zone) return;
    if (!this.world.zones.has(zone.id)) {
      this.ui.toast("这个角落正在加载，可稍后重试。");
      return;
    }
    this.interacting = true;
    this.nextInteractionAt = this.world.elapsed + 0.4;
    const step = this.interactionSteps.get(zone.id) || 0;
    this.interactionSteps.set(zone.id, step + 1);
    try {
      await this.effect(zone.id, step);
    } finally {
      this.interacting = false;
    }
  }
  async effect(id, index) {
    try {
      await this.world.effect(id, index);
      this.sound.chime();
    } catch {
      this.ui.toast("这个小互动暂时没准备好，请再试一次。");
    }
  }
  updateCamera(dt) {
    const mobile = this.aspect < 0.85;
    const overview = this.cameraMode === "overview";
    let target, position, up, span;
    if (overview) {
      const basis = frameAt(this.overviewPoint);
      target = new T.Vector3();
      position = normalAt(this.overviewPoint).multiplyScalar(110);
      up = basis.north;
      span = mobile ? 72 / this.aspect : 76;
    } else {
      const radial = this.world.position.clone().normalize();
      target = this.world.position.clone().addScaledVector(radial, 1.1);
      position = target
        .clone()
        .addScaledVector(radial, 29)
        .addScaledVector(this.world.north, -34);
      up = radial;
      span = mobile ? 15.5 / this.aspect : 25;
    }
    const blend =
      this.reduced.matches || this.cameraSnap ? 1 : 1 - Math.exp(-6 * dt);
    this.lookTarget.lerp(target, blend);
    this.camera.position.lerp(position, blend);
    this.camera.up.lerp(up, blend).normalize();
    this.currentSpan = T.MathUtils.lerp(this.currentSpan || span, span, blend);
    const h = this.currentSpan * this.zoom * 0.5;
    Object.assign(this.camera, {
      left: -h * this.aspect,
      right: h * this.aspect,
      top: h,
      bottom: -h,
    });
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.lookTarget);
    this.camera.updateMatrixWorld();
    this.cameraSnap = false;
    // Keep the sun on the visible hemisphere as the player walks around the globe.
    const lightUp = overview
      ? normalAt(this.overviewPoint)
      : this.world.position.clone().normalize();
    const lightRight = new T.Vector3().setFromMatrixColumn(
      this.camera.matrixWorld,
      0,
    );
    this.sun.position
      .copy(lightUp)
      .multiplyScalar(70)
      .addScaledVector(lightRight, -35)
      .addScaledVector(this.camera.up, 40);
  }
  occlusion() {
    this.world.fadeCamera.value.copy(this.camera.position);
    this.world.fadeTarget.value.copy(this.world.position).addScaledVector(this.world.position.clone().normalize(), 1.4);
    this.world.fadeEnabled.value = this.cameraMode === "overview" ? 0 : 1;
    if (this.cameraMode === "overview") {
      for (const object of this.occluded) {
        object.material.opacity = 1;
        object.material.depthWrite = true;
      }
      this.occluded.clear();
      return;
    }
    const direction = this.world.position
      .clone()
      .addScaledVector(this.world.position.clone().normalize(), 1.4)
      .sub(this.camera.position);
    this.ray.set(this.camera.position, direction.clone().normalize());
    this.ray.far = direction.length() - 0.7;
    const hits = this.ray.intersectObjects(
      [...this.world.zones.values()],
      true,
    );
    const current = new Set(hits.map((h) => h.object));
    for (const object of current) {
      if (!object.isMesh) continue;
      if (!this.fadeMaterials.has(object)) {
        this.fadeMaterials.set(object, object.material);
        const original = object.material;
        object.material = original.clone();
        object.material.onBeforeCompile = original.onBeforeCompile;
        object.material.customProgramCacheKey = original.customProgramCacheKey;
        object.material.transparent = true;
      }
      object.material.opacity = 0.22;
      object.material.depthWrite = false;
    }
    for (const object of this.occluded)
      if (!current.has(object)) {
        object.material.opacity = 1;
        object.material.depthWrite = true;
      }
    this.occluded = current;
    this.ray.far = Infinity;
  }
  frame(now) {
    this.frameId = requestAnimationFrame(this.frame);
    if (document.hidden) return;
    const raw = Math.max(0, (now - this.previous) / 1000);
    const dt = Math.min(Math.max(raw, 0), 0.05);
    this.previous = now;
    if (raw > 0) this.fps.push(raw);
    if (this.fps.length > 180) this.fps.shift();
    if (this.ready) {
      const moving = this.world.move(this.input.vector(), this.camera, dt);
      if (moving) {
        this.cameraMode = "follow";
        this.ui.hideIntro();
      }
      this.world.update(dt, this.reduced.matches);
      const zone = nearestZone(this.world.position);
      this.ui.setNearby(zone);
      if (zone) this.ui.active(zone.id);
    }
    this.updateCamera(dt);
    if (!this.lastOcclusion || now - this.lastOcclusion > 150) {
      this.occlusion();
      this.lastOcclusion = now;
    }
    this.renderer.render(this.scene, this.camera);
    if (!this.lastMetrics || now - this.lastMetrics > 1000) {
      this.renderer.domElement.dataset.metrics = JSON.stringify(
        this.diagnostics(),
      );
      this.lastMetrics = now;
    }
  }
  renderOnce() {
    if (this.disposed) return;
    this.renderer.render(this.scene, this.camera);
    this.renderer.domElement.dataset.metrics = JSON.stringify(
      this.diagnostics(),
    );
  }
  fallback(error) {
    this.errors.push(error?.message || "WebGL unavailable");
    if (this.frameId) cancelAnimationFrame(this.frameId);
    this.input?.setLocked(true);
    document.body.classList.add("fallback");
    document.querySelector("#fallback").hidden = false;
    this.ui.loading("");
    document.querySelector("#fallback-retry").onclick = () => location.reload();
    this.ui.bindLink(document.querySelector("#fallback-product"), "/");
    console.warn("Island fallback:", error?.message);
  }
  diagnostics() {
    return {
      ready: this.ready,
      surface: "sphere",
      radius: this.world?.position.length(),
      mode: this.cameraMode,
      position: this.world?.position.toArray(),
      loadedZones: [...(this.world?.zones.keys() || [])],
      assetFailures: [...this.assets.failures.keys()],
      low: this.low,
      drawCalls: this.renderer?.info.render.calls,
      triangles: this.renderer?.info.render.triangles,
      averageFPS: this.fps?.length
        ? 1 / (this.fps.reduce((a, b) => a + b, 0) / this.fps.length)
        : 0,
      errors: this.errors,
      animation: this.world?.animation,
    };
  }
  dispose() {
    this.disposed = true;
    this.events.abort();
    cancelAnimationFrame(this.frameId);
    removeEventListener("resize", this.onResize);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.input?.dispose();
    this.sound.dispose();
    const geometries = new Set(),
      materials = new Set(),
      textures = new Set(),
      skeletons = new Set();
    this.scene?.traverse((object) => {
      if (object.geometry) geometries.add(object.geometry);
      if (object.skeleton) skeletons.add(object.skeleton);
      for (const material of Array.isArray(object.material)
        ? object.material
        : [object.material]) {
        if (!material) continue;
        materials.add(material);
        for (const value of Object.values(material))
          if (value?.isTexture) textures.add(value);
      }
    });
    for (const resource of [
      ...geometries,
      ...materials,
      ...textures,
      ...skeletons,
    ])
      resource.dispose();
    this.renderer?.dispose();
  }
}
